// Get help, consent and cases (support-cases M1, WEB-SUP-CASE-01 … 05, WEB-SUP-GRT-01, -02; VisionPlatform
// support-access SUP-20 … SUP-27) over vision-api 0.28.0's openCase, listCases, getCase, actOnCase and the case
// messages. Who may give which access is shown, not discovered (SUP-22); the API decides.
import { ApiError } from "../../api/problem";
import type { components } from "../../api/generated/vision-api";
import type { Role } from "../organisation/roles";
import { isAdminRole } from "../organisation/roles";

export type SupportCase = components["schemas"]["SupportCase"];
export type SupportMessage = components["schemas"]["SupportMessage"];
export type CaseTopic = components["schemas"]["SupportCaseTopic"];
export type CaseState = components["schemas"]["SupportCaseState"];
export type GrantLevel = components["schemas"]["SupportGrantLevel"];
export type GrantInput = components["schemas"]["SupportGrantInput"];
type RemoteDiagnostics = components["schemas"]["RemoteDiagnostics"];
type T = (key: string, options?: Record<string, unknown>) => string;

export const TOPICS: CaseTopic[] = [
  "device_offline",
  "battery",
  "readings_wrong",
  "cannot_pair",
  "app_or_account",
  "other",
];
export const MAX_TEXT = 4000;
export const DEFAULT_DAYS = 7;
export const MAX_DAYS = 30;
/** A resolved case closes by itself this many days after it was resolved (SUP-26). */
export const REOPEN_DAYS = 7;

/** What the consent screen chose (WEB-SUP-GRT-01): no access, or readings and diagnostics on top of health. */
export interface Consent {
  access: boolean;
  readings: boolean;
  diagnostics: boolean;
  days: number;
}

export const DEFAULT_CONSENT: Consent = { access: true, readings: true, diagnostics: false, days: DEFAULT_DAYS };

/**
 * What this person may give on these devices (WEB-SUP-GRT-02, SUP-22, SUP-34). Diagnostics is offered only to an
 * Owner or Admin whose organisation is known not to say `never`: while the organisation is loading or failed to load
 * (`remoteDiagnostics` undefined) it is not offered.
 */
export interface Givable {
  /** Health and readings: `share` and `view` on every chosen device. */
  access: boolean;
  /** Diagnostics: Owner or Admin as well. */
  diagnostics: boolean;
  /** Diagnostics may not even be asked for: the organisation's remote diagnostics is `never` (SUP-34). */
  diagnosticsNever: boolean;
}

export function givable(
  role: Role | undefined,
  devices: readonly { permissions: readonly string[] }[],
  remoteDiagnostics: RemoteDiagnostics | undefined,
): Givable {
  const access =
    devices.length > 0 && devices.every((d) => d.permissions.includes("share") && d.permissions.includes("view"));
  const diagnosticsNever = remoteDiagnostics === "never";
  return {
    access,
    diagnostics: access && isAdminRole(role) && remoteDiagnostics !== undefined && !diagnosticsNever,
    diagnosticsNever,
  };
}

/**
 * The access the case asks for: null is "No access". What the person cannot give themselves is still asked for, and
 * the server turns it into a request to the organisation's Owners and Admins (openCase, SUP-22).
 */
export function grantFor(consent: Consent, can: Givable): GrantInput | null {
  if (!consent.access) return null;
  const level: GrantLevel =
    consent.diagnostics && can.diagnostics
      ? "diagnostics"
      : consent.readings || consent.diagnostics
        ? "readings"
        : "health";
  return { level, valid_days: clampDays(consent.days) };
}

/** Whether the asked access waits for an Owner or Admin (the consent screen says so before sending). */
export function needsApproval(grant: GrantInput | null, can: Givable): boolean {
  if (!grant) return false;
  return !can.access;
}

export function clampDays(days: number): number {
  return Math.min(MAX_DAYS, Math.max(1, Math.round(Number.isFinite(days) ? days : DEFAULT_DAYS)));
}

/** When access ends at the latest: `days` from now ("… or on 27 October, whichever comes first"). */
export function accessEnds(days: number, now: Date): Date {
  return new Date(now.getTime() + clampDays(days) * 86_400_000);
}

/** The day a resolved case closes by itself (SUP-26), or undefined when it is not resolved. */
export function closesOn(c: Pick<SupportCase, "state" | "resolved_at">): Date | undefined {
  if (c.state !== "resolved" || !c.resolved_at) return undefined;
  return new Date(new Date(c.resolved_at).getTime() + REOPEN_DAYS * 86_400_000);
}

/** Who wrote a message or opened a case, in words: support appears by its handle only (SUP-03, SUP-27). */
export function authorName(a: SupportMessage["author"], t: T): string {
  if (a.kind === "staff") return a.handle ? t("support.staffHandle", { handle: a.handle }) : t("support.staff");
  return a.name ?? t("support.customer");
}

/** A refusal from the support routes in words (support-cases §2.4); never swallowed. */
export function supportError(e: unknown, t: T): string {
  if (!(e instanceof ApiError)) return t("support.error.unknown");
  if (e.status === 501) return t("support.error.notYet");
  switch (e.code) {
    case "support_grant_not_allowed":
    case "remote_diagnostics_never":
    case "case_closed":
    case "reopen_window_passed":
      return t(`support.error.${e.code}`);
  }
  return e.message || t("support.error.unknown");
}

/** Whether support has access on the case now: a grant, not withdrawn, not yet ended. */
export function hasAccessNow(c: Pick<SupportCase, "grant">, now: Date): boolean {
  const g = c.grant;
  return !!g && !g.revoked_at && new Date(g.valid_until).getTime() > now.getTime();
}

export type SupportGrant = components["schemas"]["SupportGrant"];
export type AccessRequest = components["schemas"]["SupportAccessRequest"];
export const LEVELS: GrantLevel[] = ["health", "readings", "diagnostics"];

/** Who may extend, raise or withdraw a grant (SUP-33): the person who gave it, an Owner or an Admin. */
export function mayChangeGrant(g: Pick<SupportGrant, "granted_by">, me: string | undefined, role: Role | undefined) {
  return (!!me && g.granted_by.user_id === me) || isAdminRole(role);
}

/**
 * Raise to diagnostics (WEB-SUP-GRT-03, SUP-29): only when support asked for it, the person is an Owner or Admin, the
 * organisation allows diagnostics at all (SUP-34) and the grant is not diagnostics already.
 */
export function canRaise(
  c: Pick<SupportCase, "diagnostics_requested" | "remote_diagnostics" | "grant">,
  role: Role | undefined,
): boolean {
  return (
    c.diagnostics_requested && isAdminRole(role) && c.remote_diagnostics !== "never" && c.grant?.level !== "diagnostics"
  );
}

/** Whole days left on a grant, 1 to 30: what a raise or a new device keeps, since the server counts from now (Q-88). */
export function daysLeft(g: Pick<SupportGrant, "valid_until">, now: Date): number {
  return clampDays(Math.ceil((new Date(g.valid_until).getTime() - now.getTime()) / 86_400_000));
}

/** The case's devices support cannot see yet: consent is per device (SUP-36), so one added later is not covered. */
export function uncovered(c: Pick<SupportCase, "device_ids" | "grant">): string[] {
  const covered = new Set(c.grant?.device_ids ?? []);
  return c.device_ids.filter((id) => !covered.has(id));
}

/** The levels an answer may give: what was asked or lower (WEB-SUP-REQ-02); diagnostics never where it is `never`. */
export function levelsUpTo(asked: GrantLevel, remoteDiagnostics: RemoteDiagnostics | undefined): GrantLevel[] {
  // Diagnostics only once the organisation's setting is known and allows it (SUP-34).
  return LEVELS.slice(0, LEVELS.indexOf(asked) + 1).filter(
    (l) => l !== "diagnostics" || (remoteDiagnostics !== undefined && remoteDiagnostics !== "never"),
  );
}

/**
 * Whether the person can set the case's grant at this level (extend, raise, or cover a device added later): the PUT
 * covers every device of the case, so `share` and `view` on each, and Owner or Admin for diagnostics (setCaseGrant).
 */
export function canSetGrant(
  level: GrantLevel,
  role: Role | undefined,
  devices: readonly { permissions: readonly string[] }[],
  remoteDiagnostics: RemoteDiagnostics | undefined,
): boolean {
  const can = givable(role, devices, remoteDiagnostics);
  return level === "diagnostics" ? can.diagnostics : can.access;
}
